import Vue, { ref, Ref } from 'vue';

export interface UseAccessibleFocusReturn {
  setFocus: (contentRef: Ref<HTMLElement>, backupElementRef?: Ref<Vue>) => void;
  resetFocus: () => void;
}

export function useAccessibleFocus(): UseAccessibleFocusReturn {
  const focusedElBeforeOpen = ref(null);

  /**
   * Set focus on first focusable element in modal content
   * If none are found, focus on the close button
   */
  async function setFocus(
    contentRef: Ref<HTMLElement>,
    backupElementRef?: Ref<Vue>
  ): Promise<void> {
    // Save last focused element
    focusedElBeforeOpen.value = document.activeElement;

    // Wait for Vue to update modal content when shown
    await Vue.nextTick();

    const contentElement = contentRef.value;
    const allFocusableElements = contentElement?.querySelectorAll(
      'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]'
    );
    const firstElement = allFocusableElements?.[0] as HTMLElement;

    if (firstElement) {
      firstElement.focus();
      return;
    }

    // Set focus on backup element if no element to focus in main content
    const backupElement = backupElementRef.value?.$el as HTMLElement;

    backupElement?.focus();
  }

  function resetFocus(): void {
    // Return focus to last focused element
    if (focusedElBeforeOpen.value) {
      focusedElBeforeOpen.value.focus();
      focusedElBeforeOpen.value = null;
    }
  }

  return {
    setFocus,
    resetFocus
  };
}
